import React, { useEffect, useState } from "react";
import HostNav from "../components/hostComponents/HostNav";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";

import { CircularProgress } from "@mui/material";
import { getFlags, getHostProperties, getHostEarnings, getAllOrders } from "../store/thunks/hostActions";
import { buildPropertyEarningsMap } from "../utils/earningsAggregation";

function MetricCard({ title, value, icon, trend, subtitle, onClick }) {
  return (
    <div 
      className="rounded-2xl bg-gray-100 border border-gray-200 p-4 hover:bg-gray-50 transition-all duration-200 cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="p-2.5 rounded-xl bg-gray-200">
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {icon === "dollar" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />}
            {icon === "chart" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />}
            {icon === "home" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />}
            {icon === "trending" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />}
            {icon === "star" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.915a1 1 0 00.95-.69l1.519-4.674z" />}
            {icon === "orders" && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />}
          </svg>
        </div>
        {trend && (
          <div className="flex items-center space-x-1 px-2 py-1 bg-green-50 rounded-lg border border-green-200">
            <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l9.2-9.2M17 17V7H7" />
            </svg>
            <span className="text-xs font-semibold text-green-600">{trend}</span>
          </div>
        )}
      </div>
      
      <h3 className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">{title}</h3>
      <p className="text-2xl font-bold text-gray-900 mb-1">{value}</p>
      {subtitle && <p className="text-xs text-gray-400 font-medium">{subtitle}</p>}
    </div>
  );
}

function PropertyPerformanceCard({ property, earnings, index }) {
  const navigate = useNavigate();
  
  const propertyEarnings = earnings[property._id]?.earnings?.reduce((sum, amount) => sum + amount, 0) || 0;
  const orderCount = earnings[property._id]?.earnings?.length || 0;
  const avgOrderValue = orderCount > 0 ? propertyEarnings / orderCount : 0;

  return (
    <div
      className="group bg-gray-100 rounded-2xl border border-gray-200 overflow-hidden transition-all duration-200 hover:bg-gray-50 cursor-pointer"
      onClick={() => navigate(`/properties/${property._id}`)}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-bold text-gray-900">
                {property.listingTitle || `${property.address?.city} Property`}
              </h4>
              <p className="text-xs text-gray-500">
                {property.address?.line1}, {property.address?.city}
              </p>
            </div>
          </div>
          <p className="text-lg font-bold text-green-600">${propertyEarnings.toFixed(2)}</p>
        </div>
        
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-white rounded-xl border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 mb-1">Orders</p>
            <p className="text-lg font-bold text-gray-900">{orderCount}</p>
          </div>
          <div className="text-center p-3 bg-white rounded-xl border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 mb-1">Avg Value</p>
            <p className="text-lg font-bold text-gray-900">${avgOrderValue.toFixed(2)}</p>
          </div>
          <div className="text-center p-3 bg-white rounded-xl border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 mb-1">Rank</p>
            <p className="text-lg font-bold text-gray-900">#{index + 1}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TopEarningProperties({ propertyEarningsMap, properties }) {
  const navigate = useNavigate();

  const propertyEarningsData = properties
    ?.filter(p => !p.isDeleted)
    ?.map(property => {
      const totals = propertyEarningsMap?.[property._id] || {};
      const totalEarnings = totals.totalHostFee || 0;
      const orderCount = totals.orderCount || 0;
      return { ...property, totalEarnings, orderCount };
    })
    ?.filter(p => p.totalEarnings > 0)
    ?.sort((a, b) => b.totalEarnings - a.totalEarnings)
    ?.slice(0, 3);

  if (!propertyEarningsData || propertyEarningsData.length === 0) {
    return (
      <div className="bg-gray-100 rounded-2xl p-8 text-center border border-gray-200">
        <div className="w-16 h-16 bg-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">No Earnings Yet</h3>
        <p className="text-gray-500 mb-4">Property earnings will appear here once orders are completed</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-2xl p-6 border border-gray-200">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Top Earning Properties</h2>
        <Link
          to="/properties"
          className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors text-sm font-medium"
        >
          <span>View All</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {propertyEarningsData.map((property, index) => (
          <div
            key={property._id}
            className="group bg-white rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition-all duration-200 cursor-pointer"
            onClick={() => navigate(`/properties/${property._id}`)}
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <p className="text-sm font-bold text-gray-900 truncate">
                {property.listingTitle || `Property ${index + 1}`}
              </p>
            </div>
            <p className="text-lg font-bold text-green-600">${property.totalEarnings.toFixed(2)}</p>
            <p className="text-xs text-gray-500">{property.orderCount} order{property.orderCount !== 1 ? 's' : ''}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function EarningsChart({ earnings }) {
  const monthlyData = Array.from({ length: 6 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - (5 - i));
    const monthName = date.toLocaleDateString('en-US', { month: 'short' });
    
    let monthlyEarnings = 0;
    if (earnings) {
      for (const propertyId in earnings) {
        const propertyEarnings = earnings[propertyId].earnings || [];
        monthlyEarnings += propertyEarnings.reduce((sum, amount) => sum + amount, 0) * 0.15;
      }
    }
    
    return { 
      month: monthName, 
      amount: Math.round(monthlyEarnings / 6) + Math.random() * 200 
    };
  });

  const maxAmount = Math.max(...monthlyData.map(d => d.amount));

  return (
    <div className="bg-gray-100 rounded-2xl p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-1">Earnings Trend</h3>
          <p className="text-sm text-gray-500">Monthly performance</p>
        </div>
      </div>
      
      <div className="flex items-end justify-between h-32 space-x-2">
        {monthlyData.map((data, index) => {
          const height = (data.amount / maxAmount) * 100;
          return (
            <div key={index} className="flex flex-col items-center flex-1 group">
              <div 
                className="w-full bg-gray-900 rounded-t-lg transition-all duration-200 hover:bg-gray-700 cursor-pointer"
                style={{ height: `${height}%` }}
              />
              <span className="text-xs font-semibold text-gray-500 mt-2">{data.month}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuickActionsPanel({ hostInfo }) {
  const navigate = useNavigate();

  const actions = [
    { title: "Add Property", description: "List a new property", icon: "M12 6v6m0 0v6m0-6h6m-6 0H6", link: "/add-property" },
    { title: "View Orders", description: "Check all orders", icon: "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z", link: "/order-list" },
    { title: "Manage Properties", description: "Edit properties", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4", link: "/properties" },
    { title: "Settings", description: "Account settings", icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z", link: `/settings/${hostInfo?._id ?? ""}` }
  ];

  return (
    <div className="bg-gray-100 rounded-2xl p-6 border border-gray-200">
      <h3 className="text-sm font-bold text-gray-900 mb-3">Quick Actions</h3>
      <div className="grid grid-cols-2 gap-2">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={() => navigate(action.link)}
            className="group text-left p-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all duration-200"
          >
            <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center mb-2 group-hover:bg-gray-300 transition-colors">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={action.icon} />
              </svg>
            </div>
            <h4 className="font-bold text-xs text-gray-900 mb-0.5">{action.title}</h4>
            <p className="text-gray-500 text-[10px]">{action.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function EarningsDashboard({ hostInfo }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { properties, loading: propertiesLoading } = useSelector((state) => state.hostProperties);
  const { earnings, loading: earningsLoading } = useSelector((state) => state.hostEarnings);
  const { orders: allOrders, loading: allOrdersLoading } = useSelector((state) => state.orders);
  const [loading, setLoading] = useState(true);

  const propertyEarningsMap = buildPropertyEarningsMap({
    orders: allOrders,
    earningsByProperty: earnings,
  });

  let totalEarnings = 0;
  let monthlyEarnings = 0;
  let totalOrders = 0;
  let avgOrderValue = 0;

  if (propertyEarningsMap && typeof propertyEarningsMap === "object") {
    for (const propertyId in propertyEarningsMap) {
      const summary = propertyEarningsMap[propertyId] || {};
      totalEarnings += summary.totalHostFee || 0;
      totalOrders += summary.orderCount || 0;
    }

    avgOrderValue = totalOrders > 0 ? totalEarnings / totalOrders : 0;
    monthlyEarnings = totalEarnings * 0.25;
  }

  useEffect(() => {
    if (hostInfo?._id) {
      dispatch(getHostProperties(hostInfo._id));
      dispatch(getHostEarnings(hostInfo._id));
      dispatch(getAllOrders(hostInfo._id));
      setLoading(false);
    }
  }, [dispatch, hostInfo]);

  if (loading || propertiesLoading || earningsLoading || allOrdersLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#1a1a1a' }} />
          <p className="mt-4 text-gray-500 font-semibold">Loading earnings dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white overflow-auto">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-900">Earnings Overview</h2>
            <Link
              to="/host"
              className="flex items-center space-x-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              <span>Back to Dashboard</span>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard title="Total Earnings" value={`$${totalEarnings.toFixed(2)}`} icon="dollar" trend="+12.5%" subtitle="All time" onClick={() => navigate('/order-list')} />
            <MetricCard title="Monthly Average" value={`$${monthlyEarnings.toFixed(2)}`} icon="chart" trend="+8.2%" subtitle="This month" />
            <MetricCard title="Total Orders" value={totalOrders.toString()} icon="orders" trend="+15.3%" subtitle="Completed" onClick={() => navigate('/order-list')} />
            <MetricCard title="Avg Order Value" value={`$${avgOrderValue.toFixed(2)}`} icon="star" trend="+5.7%" subtitle="Per order" />
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          <div className="xl:col-span-3">
            <TopEarningProperties propertyEarningsMap={propertyEarningsMap} properties={properties} />
          </div>
          <div className="space-y-6">
            <EarningsChart earnings={earnings} properties={properties} />
            <QuickActionsPanel hostInfo={hostInfo} />
          </div>
        </div>
      </div>
    </div>
  );
}

function HostDashboard() {
  const dispatch = useDispatch();
  const { hostInfo } = useSelector((state) => state.host);
  const isOnBoarded = useSelector((state) => state.hostDetails.Info?.isOnBoarded);

  useEffect(() => {
    if (hostInfo?._id) dispatch(getFlags(hostInfo._id));
  }, [dispatch, hostInfo?._id]);

  return (
    <div className="flex h-screen w-full overflow-auto bg-white">
      <div className="mx-auto flex w-full flex-col px-6 tablet:px-0">
        <HostNav dashboard={true} hideproperty={true} />
        {isOnBoarded ? (
          <EarningsDashboard hostInfo={hostInfo} />
        ) : (
          <div className="flex items-center justify-center py-16 px-4">
            <div className="w-full max-w-md text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gray-900">
                <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">Connect Stripe to Start Earning</h3>
              <p className="text-gray-500 mb-8 max-w-sm mx-auto">
                Link your Stripe account when you are ready to receive host payouts from Bellhopt orders.
              </p>

              <Link
                to="/onboarding"
                className="block w-full rounded-xl bg-gray-900 px-6 py-4 text-base font-bold text-white transition-all duration-200 hover:bg-gray-800"
              >
                Continue setup
              </Link>
              <p className="mt-3 text-xs text-gray-400">Setup will resume at the next relevant activation step.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HostDashboard;
